import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchWorkspace, getAdminUser } from "../api/adminClient";
import { LENS_ROUTES, ROUTES } from "./routes";

const STEPS = [
  {
    id: "brand",
    title: "Brand your workspace",
    body: "Set your product name, logo, and colors for participants and reports.",
    href: ROUTES.branding,
    optional: false,
  },
  {
    id: "assessment",
    title: "Create an assessment",
    body: "Build sections and questions for your organization.",
    href: LENS_ROUTES.studioSurvey,
    optional: false,
    altHref: `${LENS_ROUTES.studioSurvey}&from=5x`,
    altLabel: "or start from the 5X Team Assessment template",
  },
  {
    id: "company",
    title: "Add a company",
    body: "Create the first company profile that will receive invites.",
    href: "/studio?add_company=1",
    optional: false,
  },
  {
    id: "invite",
    title: "Send your first invite",
    body: "Invite participants from Engagement so they can take the assessment.",
    href: LENS_ROUTES.engagementCompose,
    optional: false,
  },
  {
    id: "report",
    title: "Preview your report",
    body: "Review PDF report profiles for company and team outputs.",
    href: LENS_ROUTES.studioReports,
    optional: true,
  },
];

function StepIcon({ done }) {
  if (done) {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    );
  }
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-slate-400 text-xs font-semibold">
      ·
    </span>
  );
}

/**
 * First-run checklist for tenant workspaces on Studio → Companies.
 * Hidden for platform Super Admin and when company + custom assessment + invite exist.
 */
export default function FirstRunChecklistCard({ refreshKey = 0 }) {
  const session = getAdminUser();
  const isTenant = Boolean(session?.tenant_id) && !session?.is_platform_admin;
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(isTenant);

  const load = useCallback(async () => {
    if (!isTenant) {
      setOnboarding(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchWorkspace();
      setOnboarding(res.data?.onboarding || null);
    } catch {
      setOnboarding(null);
    } finally {
      setLoading(false);
    }
  }, [isTenant]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  if (!isTenant || loading || !onboarding?.show_checklist) {
    return null;
  }

  const stepsDone = onboarding.steps || {};
  const isFlat = onboarding.company_model === "flat";
  const visibleSteps = isFlat ? STEPS.filter((s) => s.id !== "company") : STEPS;
  const doneCount = visibleSteps.filter((s) => !s.optional && stepsDone[s.id]).length;
  const requiredTotal = visibleSteps.filter((s) => !s.optional).length;

  return (
    <section
      className="rounded-2xl border border-[#004f73]/20 bg-gradient-to-br from-[#e8f4f8] via-white to-white p-5 mb-6 shadow-sm"
      aria-label="Getting started checklist"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#004f73]">
            Getting started
          </p>
          <h2 className="text-base font-semibold text-slate-900 mt-0.5">
            Set up your workspace
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Complete these steps to run your first assessment. No 5X pack required —
            build your own flow, or optionally start from the classic template.
          </p>
        </div>
        <p className="text-sm font-medium text-slate-700 tabular-nums shrink-0">
          {doneCount}/{requiredTotal} required
        </p>
      </div>

      <ol className="space-y-3">
        {visibleSteps.map((step, index) => {
          const done = Boolean(stepsDone[step.id]);
          return (
            <li
              key={step.id}
              className={`flex gap-3 rounded-xl border px-3 py-3 ${
                done
                  ? "border-emerald-100 bg-emerald-50/40"
                  : "border-slate-200 bg-white"
              }`}
            >
              <StepIcon done={done} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">
                    {index + 1}. {step.title}
                  </p>
                  {step.optional ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Optional
                    </span>
                  ) : null}
                  {done ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
                      Done
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-slate-600 mt-0.5">{step.body}</p>
                {!done ? (
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Link
                      to={step.href}
                      className="text-sm font-semibold text-[#004f73] hover:underline"
                    >
                      {step.id === "brand"
                        ? "Open branding"
                        : step.id === "assessment"
                          ? "Create assessment"
                          : step.id === "company"
                            ? "Add company"
                            : step.id === "invite"
                              ? "Open invite wizard"
                              : "Open reports"}
                    </Link>
                    {step.altHref && step.altLabel ? (
                      <Link
                        to={step.altHref}
                        className="text-xs text-slate-500 hover:text-[#004f73] hover:underline"
                      >
                        {step.altLabel}
                      </Link>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
